/**
 * AlphaOmegaApproveQueuePage — Phase 37.9.
 *
 * Unified founder review queue aggregating three ALPHA-OMEGA approval surfaces:
 *   - Pending spawn proposals (A-01 — founder sig required, links to detail)
 *   - Unacknowledged monthly P&L (A-05 — founder sig required, links to detail)
 *   - Proposed worker allocations (no sig — inline approve)
 *
 * Items >48 h old get an urgency border. Sort: oldest-first.
 */

import { useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { fmtCents, fmtTs } from './AlphaOmegaDashboardPage';
import { fmtDelta } from './AlphaOmegaWorkerAllocationPage';
import type { SpawnProposalResponse } from './AlphaOmegaSpawnProposalsPage';
import type { MonthlyPlResponse } from './AlphaOmegaMonthlyPlPage';
import type { WorkerAllocationResponse } from './AlphaOmegaWorkerAllocationPage';

// ── Types ────────────────────────────────────────────────────────────────────

export type AoQueueItemType = 'spawn-proposal' | 'monthly-pl' | 'worker-allocation';

export interface AoQueueItem {
  item_id: string;
  item_type: AoQueueItemType;
  title: string;
  company_id: string;
  queued_at_iso: string;
  is_urgent: boolean;
  meta: string;
  allocation_id?: string;
}

interface SpawnProposalsResponse { proposals: SpawnProposalResponse[]; total: number; }
interface MonthlyPlListResponse  { entries: MonthlyPlResponse[];       total: number; }
interface WorkerAllocResponse    { allocations: WorkerAllocationResponse[]; total: number; }
interface ActionResponse         { ok: boolean; id: string; action: string; }

// ── Helpers (exported for tests) ─────────────────────────────────────────────

export function isAoUrgent(queuedAtIso: string): boolean {
  return Date.now() - new Date(queuedAtIso).getTime() > 48 * 3600 * 1000;
}

export function fmtAoQueuedAt(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export function aoItemTypeLabel(t: AoQueueItemType): string {
  switch (t) {
    case 'spawn-proposal':    return 'Proposal Spawn';
    case 'monthly-pl':        return 'Monthly CPA';
    case 'worker-allocation': return 'Workers';
  }
}

export function aoItemTypeColor(t: AoQueueItemType): string {
  switch (t) {
    case 'spawn-proposal':    return '#7dd3fc';
    case 'monthly-pl':        return '#a78bfa';
    case 'worker-allocation': return '#fbbf24';
  }
}

export function normalizeSpawnProposal(p: SpawnProposalResponse): AoQueueItem {
  return {
    item_id:      p.proposal_id,
    item_type:    'spawn-proposal',
    title:        p.title,
    company_id:   p.company_id,
    queued_at_iso: p.created_at,
    is_urgent:    isAoUrgent(p.created_at),
    meta:         fmtCents(p.budget_cents),
  };
}

export function normalizeMonthlyPl(e: MonthlyPlResponse): AoQueueItem {
  return {
    item_id:       e.pl_id,
    item_type:     'monthly-pl',
    title:         `P&L — ${fmtTs(e.period_start)}`,
    company_id:    e.company_id,
    queued_at_iso: e.created_at,
    is_urgent:     isAoUrgent(e.created_at),
    meta:          fmtCents(e.profit_cents),
  };
}

export function normalizeWorkerAlloc(a: WorkerAllocationResponse): AoQueueItem {
  const queuedAtIso = new Date(a.allocated_at * 1000).toISOString();
  return {
    item_id:       a.allocation_id,
    item_type:     'worker-allocation',
    title:         `Worker allocation — ${a.company_id.slice(0, 16)}`,
    company_id:    a.company_id,
    queued_at_iso: queuedAtIso,
    is_urgent:     isAoUrgent(queuedAtIso),
    meta:          fmtDelta(a.worker_delta),
    allocation_id: a.allocation_id,
  };
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AlphaOmegaApproveQueuePage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<'' | AoQueueItemType>('');
  const [approving, setApproving] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const spawnQuery = useQuery({
    queryKey: ['alpha-omega', 'spawn-proposals', 'pending'],
    queryFn: () => apiFetch<SpawnProposalsResponse>('/alpha-omega/spawn-proposals?status=pending'),
    refetchInterval: 30_000,
  });

  const plQuery = useQuery({
    queryKey: ['alpha-omega', 'monthly-pl', 'all'],
    queryFn: () => apiFetch<MonthlyPlListResponse>('/alpha-omega/monthly-pl'),
    refetchInterval: 30_000,
  });

  const allocQuery = useQuery({
    queryKey: ['alpha-omega', 'worker-allocation', 'proposed'],
    queryFn: () => apiFetch<WorkerAllocResponse>('/alpha-omega/worker-allocation/proposed'),
    refetchInterval: 30_000,
  });

  const isPending = spawnQuery.isPending || plQuery.isPending || allocQuery.isPending;
  const isError   = spawnQuery.isError   || plQuery.isError   || allocQuery.isError;

  const items = useMemo<AoQueueItem[]>(() => {
    const all: AoQueueItem[] = [];

    for (const p of spawnQuery.data?.proposals ?? []) {
      all.push(normalizeSpawnProposal(p));
    }
    for (const e of plQuery.data?.entries ?? []) {
      if (!e.acknowledged) all.push(normalizeMonthlyPl(e));
    }
    for (const a of allocQuery.data?.allocations ?? []) {
      all.push(normalizeWorkerAlloc(a));
    }

    const filtered = typeFilter ? all.filter(i => i.item_type === typeFilter) : all;
    return filtered.sort(
      (a, b) => new Date(a.queued_at_iso).getTime() - new Date(b.queued_at_iso).getTime(),
    );
  }, [spawnQuery.data, plQuery.data, allocQuery.data, typeFilter]);

  const urgentCount = items.filter(i => i.is_urgent).length;

  function showToast(msg: string, ok: boolean) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  }

  async function handleApproveAlloc(allocationId: string) {
    setApproving(allocationId);
    try {
      await apiFetch<ActionResponse>('/alpha-omega/worker-allocation/approve', {
        method: 'POST',
        body: { allocation_id: allocationId },
      });
      await queryClient.invalidateQueries({ queryKey: ['alpha-omega', 'worker-allocation'] });
      showToast('Allocation approved', true);
    } catch {
      showToast('Error — try again', false);
    } finally {
      setApproving(null);
    }
  }

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
  };

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',       to: '/omega' },
        { label: 'Alpha-Omega', to: '/omega/alpha-omega' },
        { label: 'Approval queue' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '11pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', direction: 'ltr' }}>
          Approval queue — Alpha-Omega
        </h1>
        {urgentCount > 0 && (
          <span style={{
            background: '#f87171', color: '#0a0d12', fontSize: '8pt', fontWeight: 700,
            minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {urgentCount}
          </span>
        )}
        {!isPending && (
          <span style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)' }}>
            {items.length}
          </span>
        )}
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value as '' | AoQueueItemType)}
          style={inputStyle}
        >
          <option value="">All</option>
          <option value="spawn-proposal">Proposals Spawn (A-01)</option>
          <option value="monthly-pl">Monthly CPA (A-05)</option>
          <option value="worker-allocation">Worker allocations</option>
        </select>
      </div>

      {/* Column headers */}
      {!isPending && !isError && items.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: '110px 1fr 100px 130px 80px',
          gap: 10, padding: '6px 12px',
          borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
          marginBottom: 2,
        }}>
          {['Type', 'Title', 'Value', 'Date', 'Action'].map(h => (
            <span key={h} style={{ fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--omega-ink-subtle)', direction: 'ltr' }}>
              {h}
            </span>
          ))}
        </div>
      )}

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading queue.</div>}

      {!isPending && !isError && items.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '40px 0', textAlign: 'center', direction: 'ltr' }}>
          No items pending approval — all handled!
        </div>
      )}

      {items.map(item => (
        <div
          key={`${item.item_type}-${item.item_id}`}
          style={{
            display: 'grid', gridTemplateColumns: '110px 1fr 100px 130px 80px',
            gap: 10, alignItems: 'center',
            padding: '10px 12px',
            borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
            borderLeft: item.is_urgent
              ? '3px solid #f87171' : '3px solid transparent',
          }}
        >
          {/* Type badge */}
          <span style={{
            fontSize: '7.5pt', padding: '1px 6px',
            border: `1px solid ${aoItemTypeColor(item.item_type)}`,
            color: aoItemTypeColor(item.item_type), borderRadius: 3,
            textAlign: 'center', direction: 'ltr',
          }}>
            {aoItemTypeLabel(item.item_type)}
          </span>

          {/* Title */}
          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: '9pt', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', direction: 'ltr' }}>
              {item.is_urgent && (
                <span style={{ color: '#f87171', marginLeft: 6, fontSize: '8pt' }}>⚠ Urgent</span>
              )}
              {item.title}
            </div>
          </div>

          {/* Meta (budget / profit / delta) */}
          <span style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink-subtle)' }}>
            {item.meta}
          </span>

          {/* Queued at */}
          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
            {fmtAoQueuedAt(item.queued_at_iso)}
          </span>

          {/* Action */}
          {item.item_type === 'worker-allocation' ? (
            <button
              disabled={approving === item.allocation_id}
              onClick={() => handleApproveAlloc(item.allocation_id!)}
              style={{
                background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.35)',
                borderRadius: 4, color: '#34d399', fontFamily: 'var(--omega-mono)',
                fontSize: '8pt', fontWeight: 600, padding: '4px 10px',
                cursor: approving === item.allocation_id ? 'default' : 'pointer',
                opacity: approving === item.allocation_id ? 0.4 : 1, direction: 'ltr',
              }}
            >
              Approve
            </button>
          ) : (
            <Link
              to={
                item.item_type === 'spawn-proposal'
                  ? `/omega/alpha-omega/spawn-proposals/${item.item_id}`
                  : `/omega/alpha-omega/monthly-pl/${item.item_id}`
              }
              style={{
                background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)',
                borderRadius: 4, color: '#fbbf24', fontFamily: 'var(--omega-mono)',
                fontSize: '8pt', fontWeight: 600, padding: '4px 10px',
                textDecoration: 'none', direction: 'ltr', whiteSpace: 'nowrap',
              }}
            >
              Review
            </Link>
          )}
        </div>
      ))}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: toast.ok ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.15)',
          border: `1px solid ${toast.ok ? '#34d399' : '#f87171'}`,
          borderRadius: 6, color: toast.ok ? '#34d399' : '#f87171',
          fontFamily: 'var(--omega-mono)', fontSize: '9pt',
          padding: '10px 20px', zIndex: 9999, direction: 'ltr',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
